/**
 * KNK-1.22 / FR-8 — invites. Rules and copy: `lib/knockInvite.ts`.
 *
 * Create, preview and accept go through joules-admin-backend: create runs AS THE CALLER, so every
 * refusal is the database's own sentence; preview and accept are public and rate limited there.
 * The list and revoke go to the database directly under the caller's session.
 */
import { supabase } from '@/lib/supabaseClient';
import {
  backendErrorMessage,
  parseCreatedInvite,
  parseInvitePreview,
  parseInviteRow,
  JOIN_COPY,
  type CreatedInvite,
  type InvitePreview,
  type InviteRow,
} from '@/lib/knockInvite';

// Same host rule as emailService.ts (CLN-1.2).
const API_BASE =
  import.meta.env.VITE_API_URL || import.meta.env.VITE_PYTHON_BACKEND_URL || 'https://admin-api.joulescrm.com';

const blank = (s: string | undefined) => (s && s.trim() ? s.trim() : null);

async function call(path: string, init: RequestInit, fallback: string): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/v1/knock-invites${path}`, init);
  } catch {
    throw new Error(JOIN_COPY.unreachable);
  }
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(backendErrorMessage(body, `${fallback} (${res.status}).`));
  return body;
}

export async function createKnockInvite(input: {
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  teamId?: number | null;
}): Promise<CreatedInvite> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in again to invite a rep.');

  const body = await call(
    '',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        phone: blank(input.phone),
        email: blank(input.email),
        first_name: blank(input.firstName),
        last_name: blank(input.lastName),
        team_id: input.teamId ?? null,
      }),
    },
    'The invite was not created',
  );
  const created = parseCreatedInvite(body);
  if (!created) throw new Error('The invite was created but its link did not come back. Invite the same person again.');
  return created;
}

export async function previewKnockInvite(code: string): Promise<InvitePreview> {
  return parseInvitePreview(
    await call(`/preview/${encodeURIComponent(code)}`, { method: 'GET' }, 'Could not check the invite'),
  );
}

/** Returns the email to sign in with: the invite's own address when it had one. */
export async function acceptKnockInvite(input: {
  code: string;
  email?: string;
  password: string;
  firstName?: string;
  lastName?: string;
}): Promise<{ email: string }> {
  const body = await call(
    '/accept',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: input.code,
        email: blank(input.email),
        password: input.password,
        first_name: blank(input.firstName),
        last_name: blank(input.lastName),
      }),
    },
    'Could not join',
  );
  const email = (body as { email?: unknown } | null)?.email;
  if (typeof email !== 'string' || !email) {
    throw new Error('You joined, but the sign-in email did not come back. Sign in to the Joules app with your email.');
  }
  return { email };
}

/** The invites the caller may see (their own, or all of the account's for an admin), newest first. */
export async function listKnockInvites(limit = 50): Promise<InviteRow[]> {
  const { data, error } = await supabase
    .from('knock_invite_list')
    .select('id,status,first_name,last_name,phone,email,team_name,created_at,expires_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []).map(parseInviteRow).filter((r): r is InviteRow => r !== null);
}

export async function revokeKnockInvite(inviteId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_knock_invite', { p_invite_id: inviteId });
  if (error) throw new Error(error.message);
}
